import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  makeStyles,
  InputAdornment,
  Tooltip,
  Checkbox,
  ListItemText,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { Autocomplete } from "@material-ui/lab";
import {  Edit, GetApp, Search, Clear } from "@material-ui/icons";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { fetchAllRMPMCodes } from "../../../containers/actions/common/inventoryStatus";
import { appUrl } from "../../../../../utils/config";
import ViewReconciliationHistory from "./ViewReconciliationHistory";
import { convertToLocaleDateTime } from "../functionalComponents/FormatDateTimeToLocale";

const useStyles = makeStyles((theme) => ({
  selectSearchField: {
    border: "1px solid black",
    borderRight: "none",
    borderRadius: "0.1rem",
    backgroundColor: "#F0F0F0",
    minWidth: "190px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  searchTextField: {
    flex: 1,
    minWidth: "330px",
    border: "1px solid black",
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "8px",
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  outlineButton: {
    borderRadius: "2rem",
    whiteSpace: "nowrap",
    cursor: "pointer",
    borderColor: "black",
    textTransform: "none",
    padding: "8px 12px",
    fontSize: "0.875rem",
    "&:hover": {
      backgroundColor: "#000000",
      color: "#fff",
      textTransform: "none",
    },
  },
  downloadButton: {
    borderRadius: "2rem",
    whiteSpace: "nowrap",
    cursor: "pointer",
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    padding: "8px 12px",
    fontSize: "0.875rem",
    "&:hover": {
      backgroundColor: "#000000",
      color: "#fff",
      textTransform: "none",
    },
  },
  autocompleteInput: {
    "& .MuiAutocomplete-clearIndicator": {
      marginRight: "3px",
    },
  },
  clearText: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": {
      color: "#173072",
      textDecoration: "underline",
    },
  },
}));

function ReconciliationOfInventoryDialog(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { onBack, onClose, cmoName, apiPayload, selectedCMO, sponsorLastUpdatedDate } = props;
  const [searchBy, setSearchBy] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [plantList, setPlantList] = useState([]);
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [materialCodesList, setMaterialCodesList] = useState([]);
  const [selectedMaterialCodes, setSelectedMaterialCodes] = useState(null);
  const [materialTypesList, setMaterialTypesList] = useState([]);
  const [selectedMaterialTypes, setSelectedMaterialTypes] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [cmoLastUpdatedDate, setCMOLastUpdatedDate] = useState(null);

  const filterOptions = [
    { label: "BMS on-hand qty. = 0", value: "BMS On-hand Qty equals 0" },
    { label: "Difference (BMS - CMO) = 0", value: "Difference(BMS-CMO) equals 0" },
    { label: "Difference (BMS - CMO) ≠ 0", value: "Difference(BMS-CMO) not equals 0" },
  ];
  const [reconHistoryOpen, setReconHistoryOpen] = useState(false);

  const materialCodesData = useSelector((state) =>
    state?.pcmp?.common?.inventoryStatus?.materialCodesDetails
  );

  const fetchReconciliationData = async (customPayload = null) => {
    try {
      const payload = customPayload || apiPayload;
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchInventoryReconciliationData`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      setCMOLastUpdatedDate(response.data?.cmoLatestInventorySyncTime)
      return {
        data: response.data?.inventoryReconciliationData || [],
        total: response.data?.inventoryReconciliationData?.length || 0
      };
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
      return {
        data: [],
        total: 0
      };
    }
  };

  const [reconciliationData, setReconciliationData] = useState([]);

  const [editingRowId, setEditingRowId] = useState(null);
  const [editedReasons, setEditedReasons] = useState({});
  const [displayCount, setDisplayCount] = useState(25);
  const [allRecords, setAllRecords] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [collabrixReconciliationID, setCollabrixReconciliationID] = useState(0);
  const [savingIndex, setSavingIndex] = useState(null);

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
    if (onBack) {
      onBack();
    }
  };

  const handleEditReason = (row, index) => {
    setEditingRowId(index);
    setEditedReasons((prev) => ({ ...prev, [index]: row.reasonForDifference }));
  };

  const handleSaveReason = async (index) => {
    setSavingIndex(index);
    try {
      const row = reconciliationData[index];
      const payload = {
        collabrixBatchReconciliationID: row.collabrixBatchReconciliationID,
        reasonForDifference: editedReasons[index] ?? row.reasonForDifference,
        vendorMosymphonyID: apiPayload?.vendorMosymphonyID,
        brandOwnerMosymphonyID: apiPayload?.brandOwnerMosymphonyID,
        clearReasonForDifference: false,
        collabrixReconciliationID: collabrixReconciliationID,
      };

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateInventoryReconciliationData`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (response.data?.status) {
        // Refresh data from API on success
        const refreshPayload = { ...apiPayload };
        if (collabrixReconciliationID !== 0) {
          refreshPayload.collabrixReconciliationID = collabrixReconciliationID;
        }
        
        const result = await fetchReconciliationData(refreshPayload);
        setAllRecords(result.data);
        setReconciliationData(result.data);
        setEditingRowId(null);
      } else {
        console.error("Failed to update reason:", response.data?.message);
        alert("Failed to update reason. Please try again.");
      }
    } catch (error) {
      console.error("Error updating reason:", error);
    } finally {
      setSavingIndex(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
  };

  const handleClearReasons = async () => {
    setIsInitialLoading(true);
    try {
      const payload = {
        brandOwnerMosymphonyID: apiPayload?.brandOwnerMosymphonyID,
        clearReasonForDifference: true,
        vendorMosymphonyID: apiPayload?.vendorMosymphonyID,
        collabrixReconciliationID: collabrixReconciliationID,
        reasonForDifference: ""
      };

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateInventoryReconciliationData`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (response.data?.status) {
        // Refresh data from API on success
        const refreshPayload = { ...apiPayload };
        if (collabrixReconciliationID !== 0) {
          refreshPayload.collabrixReconciliationID = collabrixReconciliationID;
        }
        
        const result = await fetchReconciliationData(refreshPayload);
        setAllRecords(result.data);
        setReconciliationData(result.data);
      } else {
        console.error("Failed to clear reasons:", response.data?.message);
        alert("Failed to clear reason for difference. Please try again.");
      }
    } catch (error) {
      console.error("Error clearing reasons:", error);
      alert("An error occurred while clearing. Please try again.");
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleClearFilters = async () => {
    // Clear all filter states
    setSearchBy("all");
    setSearchValue("");
    setSelectedPlants([]);
    setSelectedMaterialCodes(null);
    setSelectedMaterialTypes([]);
    setSelectedFilters(null);

    // Fetch data with original payload
    setIsInitialLoading(true);
    try {
      let clearPayload = { ...apiPayload };
      if (collabrixReconciliationID !== 0) {
        clearPayload.collabrixReconciliationID = collabrixReconciliationID;
      }
      
      const result = await fetchReconciliationData(clearPayload);
      setAllRecords(result.data);
      setReconciliationData(result.data);
      setDisplayCount(25);
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight - scrollTop - clientHeight < 100 && displayCount < allRecords.length) {
      loadMoreRows();
    }
  };

  const loadMoreRows = () => {
    setDisplayCount(prev => Math.min(prev + 25, allRecords.length));
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setIsInitialLoading(true);
      try {
        const result = await fetchReconciliationData();
        setAllRecords(result.data);
        setReconciliationData(result.data);
        // Store collabrixReconciliationID from first record
        if (result.data && result.data.length > 0 && result.data[0].collabrixReconciliationID) {
          setCollabrixReconciliationID(result.data[0].collabrixReconciliationID);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (apiPayload) {
      loadInitialData();
    }
  }, [apiPayload]);

  useEffect(() => {
    dispatch(fetchAllRMPMCodes());
    axios
      .get(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchAllReceivingPlantIDsAndStorageLocationIDs/all`
      )
      .then((res) => {
        if (res.data?.vendorOrgLevelData) {
          const selectedVendorData = res.data.vendorOrgLevelData.find(
            (item) => item.vendorMosymphonyID === apiPayload?.vendorMosymphonyID
          );

          if (selectedVendorData && selectedVendorData.receivingPlantIDs) {
            const formattedPlants = selectedVendorData.receivingPlantIDs
              .filter((id) => id)
              .map((id) => ({
                plantCode: id,
                vendorMosymphonyID: selectedVendorData.vendorMosymphonyID,
              }));

            setPlantList(formattedPlants);
          } else {
            setPlantList([]);
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching plant data:", err);
      });

    axios
      .get(`${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchMaterialTypes/true`)
      .then((res) => {
        if (res.data?.status && res.data?.materialTypeList) {
          const activatedTypes = res.data.materialTypeList.filter(
            (type) => type.activated === true
          );
          setMaterialTypesList(activatedTypes);
        }
      })
      .catch((err) => {
        console.error("Error fetching material types:", err);
      });
  }, [dispatch]);

  useEffect(() => {
    if (materialCodesData?.data) {
      const filteredMaterialCodes = materialCodesData.data.filter(
        (material) => material.vendorMosymphonyID === apiPayload?.vendorMosymphonyID
      );
      setMaterialCodesList(filteredMaterialCodes);
    }
  }, [materialCodesData, apiPayload?.vendorMosymphonyID]);

  const handleDownloadExcel = async () => {
    try {
      let downloadPayload = { ...apiPayload };
      // Use stored collabrixReconciliationID if available
      if (collabrixReconciliationID !== 0) {
        downloadPayload.collabrixReconciliationID = collabrixReconciliationID;
      }
      
      if (searchBy === "Plant ID" && selectedPlants.length > 0) {
        downloadPayload.vendorPlant = selectedPlants.map(p => p.plantCode);
      } else if (searchBy === "Material Type" && selectedMaterialTypes.length > 0) {
        downloadPayload.materialTypeID = selectedMaterialTypes.map(t => t.materialTypeID);
      } else if (searchBy === "Filter rows with" && selectedFilters) {
        downloadPayload.filterRowsWith = selectedFilters.value;
      } else if (searchBy === "BMS Material Code" && selectedMaterialCodes) {
        downloadPayload.sponsorMaterialCode = selectedMaterialCodes.materialCode;
      }

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/inventoryReconciliationDownloadExcel`,
        downloadPayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });


      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Inventory_Reconciliation_${cmoName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Error downloading excel:", error);
      alert("Failed to download excel file. Please try again.");
    }
  };

  return (
    <Dialog open={true} fullScreen>
      <DialogContent style={{ padding: "0", height: "90vh", display: "flex", flexDirection: "column" }}>
        <Box style={{ padding: "20px", paddingTop: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Header */}
          <Box
            style={{ marginTop: "1em", display: "flex", gap: "1em" }}
          // onClick={handleBack}
          >
            <Typography
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={handleBack}
            >
              <ArrowBackIosIcon />
              Back
            </Typography>
            <Typography variant='h5' style={{ fontWeight: 600 }}>
              Reconciliation of Inventory
            </Typography>
          </Box>
          <Divider style={{ margin: "1em 0" }} />

          {/* First Row: Search + Action Buttons */}
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
            {/* Search Section */}
            <Box style={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
              <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: "10px" }}>
                Search by:
              </Typography>
              <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <TextField
                    size="small"
                    select
                    value={searchBy}
                    onChange={(event) => {
                      setSearchBy(event.target.value);
                      setSelectedPlants([]);
                      setSelectedMaterialCodes(null);                      
                      setSelectedMaterialTypes([]);
                      setSelectedFilters(null);
                      setSearchValue("");
                    }}
                    variant="outlined"
                    className={classes.selectSearchField}
                  >
                    <MenuItem value="all">Select Criteria</MenuItem>
                    <MenuItem value="Filter rows with">Filter rows with</MenuItem>
                    <MenuItem value="Material Type">Material Type</MenuItem>
                    <MenuItem value="Plant ID">Plant ID</MenuItem>
                    <MenuItem value="BMS Material Code">BMS Material Code</MenuItem>
                  </TextField>

                  {(searchBy === "Plant ID" || searchBy === "Material Type" || searchBy === "Filter rows with") ? (
                    <Box style={{ position: "relative", flex: 1, minWidth: "330px", width: "100" }}>
                      <FormControl
                        fullWidth
                        style={{
                          width: "100%",
                          height: "40px",
                          minHeight: "40px",
                          maxHeight: "40px",
                          border: "1px solid #ccc",
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                          borderTopLeftRadius: "0px",
                          borderBottomLeftRadius: "0px",
                          overflow: "hidden",
                        }}
                      >
                        <Select
                          multiple={searchBy !== "Filter rows with"}
                          fullWidth
                          variant="outlined"
                          disableUnderline
                          value={searchBy === "Plant ID" ? selectedPlants : 
                                 searchBy === "Material Type" ? selectedMaterialTypes :
                                 searchBy === "Filter rows with" ? (selectedFilters || "") : []}
                          onChange={(e) => {
                            let value = e.target.value;
              
                            if (searchBy === "Filter rows with") {
                              // Single select - value is the object directly
                              setSelectedFilters(value);
                              if (value) {
                                setSearchValue(value.value);
                              } else {
                                setSearchValue("");
                              }
                            } else {
                              // Multiple select - ensure value is array
                              if (!Array.isArray(value)) {
                                value = value ? [value] : [];
                              }
                              
                              if (searchBy === "Plant ID") {
                                setSelectedPlants(value);
                                if (value.length > 0) {
                                  const plantCodes = value.map(plant => plant.plantCode);
                                  setSearchValue(plantCodes);
                                } else {
                                  setSearchValue([]);
                                }
                              } else if (searchBy === "Material Type") {
                                setSelectedMaterialTypes(value);
                                if (value.length > 0) {
                                  const materialTypeNames = value.map(type => type.materialTypeName);
                                  setSearchValue(materialTypeNames);
                                } else {
                                  setSearchValue([]);
                                }
                              }
                            }
                          }}
                          displayEmpty
                          renderValue={(selected) => {
                            if (searchBy === "Filter rows with") {
                              return selected ? selected.label : <span className="text-muted" style={{color:'#6c757d'}}>Select {searchBy}</span>;
                            }
                            if (selected.length === 0) {
                              return <span className="text-muted" style={{ color: '#6c757d' }}>Select {searchBy}</span>;
                            } else if (selected.length === 1) {
                              return searchBy === "Plant ID" ? selected[0].plantCode : 
                                     searchBy === "Material Type" ? selected[0].materialTypeName : selected[0];
                            } else {
                              return "Multiple selection";
                            }
                          }}
                          style={{
                            height: "40px",
                            minHeight: "40px",
                            maxHeight: "40px",
                            padding: "8px 50px 8px 14px",
                            width: "100%",
                            overflow: "hidden",
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                overflowY: 'auto',
                              },
                            },
                          }}
                        >
                          {(searchBy === "Plant ID" ? plantList : 
                            searchBy === "Material Type" ? materialTypesList :
                            searchBy === "Filter rows with" ? filterOptions : []).length > 0 ? (
                            (searchBy === "Plant ID" ? plantList : 
                             searchBy === "Material Type" ? materialTypesList :
                             searchBy === "Filter rows with" ? filterOptions : []).map((item, index) => (
                              <MenuItem key={index} value={item}>
                                {searchBy !== "Filter rows with" && (
                                  <Checkbox
                                    checked={
                                      searchBy === "Plant ID" 
                                        ? selectedPlants.some(selected => selected.plantCode === item.plantCode)
                                        : selectedMaterialTypes.some(selected => selected.materialTypeID === item.materialTypeID)
                                    }
                                  />
                                )}
                                <ListItemText 
                                  primary={
                                    searchBy === "Plant ID" ? item.plantCode : 
                                    searchBy === "Material Type" ? item.materialTypeName :
                                    searchBy === "Filter rows with" ? item.label : ""
                                  } 
                                />
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>No {searchBy} available</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                      <Box
                        style={{
                          position: "absolute",
                          right: "-1px",
                          top: "0px",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          pointerEvents: "none",
                          zIndex: 1
                        }}
                      >
                        <Box style={{ pointerEvents: "auto" }}>
                          {(searchBy === "Plant ID" ? selectedPlants.length > 0 : 
                            searchBy === "Material Type" ? selectedMaterialTypes.length > 0 :
                            searchBy === "Filter rows with" ? selectedFilters !== null : false) && (
                            <Tooltip title="Clear selection" aria-label="clear">
                              <Clear
                                fontSize="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                
                                  if (searchBy === "Plant ID") {
                                    setSelectedPlants([]);
                                  } else if (searchBy === "Material Type") {
                                    setSelectedMaterialTypes([]);
                                  } else if (searchBy === "Filter rows with") {
                                    setSelectedFilters(null);
                                  }
                                  setSearchValue("");
                                }}
                                style={{
                                  cursor: "pointer",
                                  color: "dimgray",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Box style={{ pointerEvents: "auto" }}>
                          <IconButton
                            size="small"
                            style={{
                              backgroundColor: "#EB5E28",
                              color: "white",
                              borderRadius: "4px",
                              minWidth: "32px",
                              height: "40px",
                              padding: "8px",
                              borderTopRightRadius: "7px",
                              borderBottomRightRadius: "7px",
                              borderTopLeftRadius: "0px",
                              borderBottomLeftRadius: "0px",
                              margin: "0px",
                            }}
                            disabled={
                              searchBy === "Plant ID" ? selectedPlants.length === 0 : 
                              searchBy === "Material Type" ? selectedMaterialTypes.length === 0 :
                              searchBy === "Filter rows with" ? !selectedFilters : true
                            }
                            onClick={async () => {
                              setIsInitialLoading(true);
                              try {
                                let searchPayload = { ...apiPayload };
                                // Use stored collabrixReconciliationID if available
                                if (collabrixReconciliationID !== 0) {
                                  searchPayload.collabrixReconciliationID = collabrixReconciliationID;
                                }
                                
                                if (searchBy === "Plant ID") {
                                  searchPayload.vendorPlant = selectedPlants.map(p => p.plantCode);
                                } else if (searchBy === "Material Type") {
                                  searchPayload.materialTypeID = selectedMaterialTypes.map(t => t.materialTypeID);
                                } else if (searchBy === "Filter rows with") {
                                  searchPayload.filterRowsWith = selectedFilters.value;
                                }

                                const result = await fetchReconciliationData(searchPayload);
                                setAllRecords(result.data);
                                setReconciliationData(result.data);
                                setDisplayCount(25);
                              } catch (error) {
                                console.error("Error fetching reconciliation data:", error);
                              } finally {
                                setIsInitialLoading(false);
                              }
                            }}
                          >
                            <Search />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ) : searchBy === "BMS Material Code" ? (
                    <Box style={{ position: "relative", flex: 1, minWidth: "330px", width: "100%" }}>
                      <Autocomplete
                        className={classes.autocompleteInput}
                        options={materialCodesList}
                        getOptionLabel={(option) => `${option.materialCode} / ${option.materialDescription}`}
                        value={selectedMaterialCodes}
                        onChange={(event, newValue) => {
                          setSelectedMaterialCodes(newValue);
                          if (newValue) {
                            setSearchValue(newValue.materialCode);
                          } else {
                            setSearchValue("");
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Type to search material code"
                            size="small"
                            style={{
                              border: "1px solid #ccc",
                              borderTopRightRadius: "8px",
                              borderBottomRightRadius: "8px",
                              borderTopLeftRadius: "0px",
                              borderBottomLeftRadius: "0px",
                              backgroundColor: "#fff",
                            }}
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                height: "40px",
                                padding: "0 50px 0 8px",
                              },
                            }}
                            inputProps={{
                              ...params.inputProps,
                              style: {
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                maxWidth: "92%"
                              },
                            }}
                          />
                        )}
                        ListboxProps={{
                          style: {
                            maxHeight: 300,
                          },
                        }}
                      />
                      <Box
                        style={{
                          position: "absolute",
                          right: "-1px",
                          top: "0px",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          pointerEvents: "none",
                          zIndex: 1
                        }}
                      >
                        <Box style={{ pointerEvents: "auto" }}>
                          <IconButton
                            size="small"
                            style={{
                              backgroundColor: "#EB5E28",
                              color: "white",
                              borderRadius: "4px",
                              minWidth: "32px",
                              height: "40px",
                              padding: "8px",
                              borderTopRightRadius: "7px",
                              borderBottomRightRadius: "7px",
                              borderTopLeftRadius: "0px",
                              borderBottomLeftRadius: "0px",
                              margin: "0px",
                            }}
                            disabled={!selectedMaterialCodes}
                            onClick={async () => {
                              setIsInitialLoading(true);
                              try {
                                let searchPayload = { ...apiPayload };
                                // Use stored collabrixReconciliationID if available
                                if (collabrixReconciliationID !== 0) {
                                  searchPayload.collabrixReconciliationID = collabrixReconciliationID;
                                }
                                searchPayload.sponsorMaterialCode = selectedMaterialCodes.materialCode;
                                
                                const result = await fetchReconciliationData(searchPayload);
                                setAllRecords(result.data);
                                setReconciliationData(result.data);
                                setDisplayCount(25);
                              } catch (error) {
                                console.error("Error fetching reconciliation data:", error);
                              } finally {
                                setIsInitialLoading(false);
                              }
                            }}
                          >
                            <Search />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <TextField
                      size="small"
                      value={searchValue}
                      disabled={searchBy === "all"}
                      onChange={(e) => {
                        if (searchBy !== "all") {
                          setSearchValue(e.target.value);
                        }
                      }}
                      onBlur={(e) => {
                        const { value } = e.target;
                        if (searchBy !== "all" && typeof value === "string" && value.trim() !== value) {
                          setSearchValue(value.trim());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          // Handle search
                        }
                      }}
                      variant="outlined"
                      placeholder="Search"
                      className={classes.searchTextField}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {searchValue && (
                              <Tooltip title="Clear search" aria-label="clear">
                                <Clear
                                  fontSize="small"
                                  onClick={() => setSearchValue("")}
                                  style={{
                                    cursor: "pointer",
                                    color: "dimgray",
                                  }}
                                />
                              </Tooltip>
                            )}
                            <IconButton
                              size="small"
                              style={{
                                backgroundColor: "#EB5E28",
                                color: "white",
                                borderTopRightRadius: "7px",
                                borderBottomRightRadius: "7px",
                                borderTopLeftRadius: "0px",
                                borderBottomLeftRadius: "0px",
                                padding: "8px",
                                translate: "0.84rem",
                                marginLeft: "0px",
                                marginRight: "0px",
                              }}
                              onClick={() => {
                                // Handle search click
                              }}
                            >
                              <Search />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  style={{
                    fontSize: 12,
                    color: "blue",
                    marginLeft: "5px",
                    visibility:
                      searchBy === "all" &&
                      selectedPlants.length === 0 &&
                      selectedMaterialTypes.length === 0 &&
                      !selectedFilters &&
                      !selectedMaterialCodes
                        ? "hidden"
                        : "visible",
                  }}
                  className={classes.clearText}
                  onClick={handleClearFilters}
                >
                  Clear Filter
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box style={{ display: "flex", gap: "10px", alignItems: "center", alignSelf: "flex-end" }}>
              <Button variant="outlined" className={classes.outlineButton} onClick={() => setReconHistoryOpen(true)}>
                View Reconciliation History
              </Button>
              <Button 
                variant="outlined" 
                className={classes.outlineButton}
                onClick={handleClearReasons}
              >
                Clear Reason for Difference Column Values
              </Button>
              <Button
                variant="contained"
                startIcon={<GetApp />}
                className={classes.downloadButton}
                onClick={handleDownloadExcel}
              >
                Download excel
              </Button>
            </Box>
          </Box>

          {/* Second Row: Note + Inventory Status */}
          <Box style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", margin: "20px 0" }}>
            {/* Note */}
            <Typography variant="body2" style={{ maxWidth: "60%", fontSize: "14px" }}>
              Note - If CMO UoM is not same as BMS UoM, Mosymphony converts the quantities to match BMS UoM and display these values in the Inventory Reconciliation dashboard.
            </Typography>

            {/* Inventory Status */}
            <Box style={{ display: "flex", flexDirection: "column", gap: "5px", alignItems: "flex-end" }}>
              {sponsorLastUpdatedDate && <Typography variant="body2" style={{ fontSize: "14px" }}>
                BMS inventory - {convertToLocaleDateTime(sponsorLastUpdatedDate)}
              </Typography>}
              {cmoLastUpdatedDate && <Typography variant="body2" style={{ fontSize: "14px" }}>
                CMO inventory - {convertToLocaleDateTime(cmoLastUpdatedDate)}
              </Typography>}
            </Box>
          </Box>

          {/* Reconciliation Table */}
          <TableContainer
            component={Paper}
            style={{ flex: 1, overflow: "auto" }}
            onScroll={handleScroll}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Plant ID</TableCell>
                  <TableCell>Material Code / Description</TableCell>
                  <TableCell>BMS UoM</TableCell>
                  <TableCell>BMS Batch Number</TableCell>
                  <TableCell>CMO Batch Number</TableCell>
                  <TableCell align="right">BMS on-hand qty.</TableCell>
                  <TableCell align="right">CMO on-hand qty.</TableCell>
                  <TableCell align="right">Difference (BMS - CMO)</TableCell>
                  <TableCell style={{ width: "300px", maxWidth: "300px" }}>Reason for Difference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isInitialLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ padding: "40px" }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : reconciliationData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ padding: "40px" }}>
                      <Typography variant="body2" color="textSecondary">
                        No records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {reconciliationData.slice(0, displayCount).map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.vendorPlant}</TableCell>
                        <TableCell>
                          {row.sponsorMaterialCode && row.sponsorMaterialDescription 
                            ? `${row.sponsorMaterialCode} / ${row.sponsorMaterialDescription}`
                            : row.sponsorMaterialCode || row.sponsorMaterialDescription || ''}
                        </TableCell>
                        <TableCell>{row.sponsorUOM}</TableCell>
                        <TableCell>{row.sponsorBatchNumber}</TableCell>
                        <TableCell>{row.cmoBatchNumber}</TableCell>
                        <TableCell align="right">{row.sponsorOnHandQty != null ? (Number(row.sponsorOnHandQty) % 1 !== 0 ? Number(row.sponsorOnHandQty).toFixed(3) : row.sponsorOnHandQty?.toLocaleString()) : ''}</TableCell>
                        <TableCell align="right">{row.cmoOnHandQty != null ? (Number(row.cmoOnHandQty) % 1 !== 0 ? Number(row.cmoOnHandQty).toFixed(3) : row.cmoOnHandQty?.toLocaleString()) : ''}</TableCell>
                        <TableCell align="right">
                          {row.difference != null ? (Number(row.difference) % 1 !== 0 ? Number(row.difference).toFixed(3) : row.difference) : ''}
                        </TableCell>
                    <TableCell style={{ width: "300px", maxWidth: "300px", wordWrap: "break-word", whiteSpace: "normal" }}>
                      {editingRowId === index ? (
                        <Box style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            variant="outlined"
                            value={editedReasons[index] ?? row.reasonForDifference}
                            onChange={(e) =>
                              setEditedReasons((prev) => ({ ...prev, [index]: e.target.value }))
                            }
                            disabled={savingIndex === index}
                            style={{
                              backgroundColor: "#fff",
                            }}
                          />
                          <Box style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <Tooltip title="Cancel" aria-label="cancel">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                style={{ padding: "4px" }}
                                disabled={savingIndex === index}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Save" aria-label="save">
                              <IconButton 
                                size="small" 
                                onClick={() => handleSaveReason(index)}
                                style={{ padding: "4px" }}
                                disabled={savingIndex === index}
                              >
                                {savingIndex === index ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SaveIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : (
                        <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                          <span>{row.reasonForDifference}</span>
                          <Tooltip title="Edit reason" aria-label="edit">
                            <IconButton size="small" onClick={() => handleEditReason(row, index)}>
                              <Edit style={{ color: "#ff6b35" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {displayCount < reconciliationData.length && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ padding: "20px" }}>
                      <Typography variant="body2" color="textSecondary">
                        Showing {displayCount} of {reconciliationData.length} records. Scroll down to load more...
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {reconHistoryOpen && <ViewReconciliationHistory
          open={reconHistoryOpen}
          onClose={() => setReconHistoryOpen(false)}
          vendorMosymphonyID={selectedCMO?.organizationId === "All" ? undefined : selectedCMO?.organizationId}
          cmoName={selectedCMO?.organizationName === "All" ? "" : (selectedCMO?.organizationName ?? "")}
        />}
      </DialogContent>
    </Dialog>
  );
}

export default ReconciliationOfInventoryDialog;